<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Sync TRT patients</h1>
      <p class="subtitle">
        Pulls the latest info from your EMR for each patient in the selected
        stages, one at a time and oldest sync first. Oldest sync:
        <strong>{{ oldestSyncLabel }}</strong>
      </p>
    </div>

    <div class="modal-card-body">
      <div class="stage-filter">
        <label class="form-label">Stages to sync</label>
        <SelectInput
          :modelValue="selectedStages"
          @update:modelValue="selectedStages = $event"
          :options="stageOptions"
          :multiSelect="true"
          :disabled="isSyncing"
          placeholder="Select stages…"
        />
      </div>

      <div v-if="isSyncing || isDone" class="progress-section">
        <div class="progress-info">
          <span class="progress-label">{{ statusLabel }}</span>
          <span class="progress-count">{{ progress.current }} / {{ progress.total }}</span>
        </div>
        <div class="progress-bar-container">
          <div class="progress-bar" :style="{ width: progressPercentage + '%' }"></div>
        </div>
      </div>

      <div v-if="isDone" class="summary">
        <div class="summary-counts">
          <span class="summary-chip ok">{{ okCount }} synced</span>
          <span v-if="skippedCount" class="summary-chip skipped">{{ skippedCount }} skipped</span>
          <span v-if="errorCount" class="summary-chip error">{{ errorCount }} failed</span>
        </div>
        <ul v-if="issues.length" class="summary-list">
          <li v-for="(r, i) in issues" :key="i" :class="r.status">
            <span class="summary-name">{{ r.name }}</span>
            <span class="summary-message">{{ r.message }}</span>
          </li>
        </ul>
      </div>
    </div>

    <div class="modal-card-footer">
      <template v-if="isDone">
        <button class="button primary form-button close-on-click">Done</button>
      </template>
      <template v-else-if="isSyncing">
        <button class="button secondary form-button" disabled>Syncing…</button>
      </template>
      <template v-else>
        <button class="button primary form-button" @click="run" :disabled="progress.total === 0">
          Sync {{ progress.total }} patient{{ progress.total === 1 ? '' : 's' }}
        </button>
      </template>
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import SelectInput from '@/components/inputs/SelectInput.vue';
import { getEmrMedications, getEmrDocuments, getEmrLabs } from '@/libs/extensionBridge';
import { installEmrStub } from '@/libs/emrExtensionStub';
import { queryClient } from '@/libs/queryClient';
import * as api from '@/api';
import * as formatUtils from '@/utils/formatters';
import { COLUMN, COLUMNS, getBoardColumn } from '@/utils/trt';

const SYNC_DELAY_MS = 800;

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export default {
  name: 'SyncTRTModal',
  components: { CloseIcon, SelectInput },
  props: {
    // The loaded TRT cards to sync (the dashboard's flat patients array).
    patients: {
      type: Array,
      default: () => []
    }
  },
  emits: ['done'],
  data() {
    return {
      selectedStages: [COLUMN.ONBOARDING, COLUMN.UPCOMING],
      progress: { current: 0, total: 0 },
      results: [],
      isSyncing: false,
      isDone: false
    };
  },
  computed: {
    stageOptions() {
      return COLUMNS.map((c) => ({ value: c.key, label: c.label }));
    },
    // Patients whose board column is in the selected stages.
    filteredPatients() {
      const stages = this.selectedStages || [];
      return (this.patients || []).filter((p) => stages.includes(getBoardColumn(p)));
    },
    // Selected cards ordered oldest-synced first (never-synced sort to the top).
    sortedCards() {
      return [...this.filteredPatients].sort(
        (a, b) => this.syncTimestamp(a) - this.syncTimestamp(b)
      );
    },
    syncedTimestamps() {
      return this.filteredPatients
        .map((p) => p.trtDataDate)
        .filter(Boolean)
        .map((d) => new Date(d).getTime())
        .filter((t) => !Number.isNaN(t));
    },
    oldestSyncLabel() {
      const ts = this.syncedTimestamps;
      if (!ts.length) return 'Never';
      return formatUtils.formatDate(new Date(Math.min(...ts)).toISOString());
    },
    progressPercentage() {
      if (this.progress.total === 0) return 0;
      return Math.round((this.progress.current / this.progress.total) * 100);
    },
    statusLabel() {
      if (this.isDone) return 'Sync complete';
      if (this.isSyncing) return 'Syncing patients…';
      return 'Ready to sync';
    },
    okCount() {
      return this.results.filter((r) => r.status === 'ok').length;
    },
    skippedCount() {
      return this.results.filter((r) => r.status === 'skipped').length;
    },
    errorCount() {
      return this.results.filter((r) => r.status === 'error').length;
    },
    issues() {
      return this.results.filter((r) => r.status !== 'ok');
    }
  },
  created() {
    // Stand in for the extension's EMR responder until the real one ships.
    installEmrStub();
    // Don't sync automatically — wait for the user to click "Run sync".
    this.progress = { current: 0, total: this.sortedCards.length };
  },
  watch: {
    // Re-scope the pending run whenever the selected stages change.
    selectedStages() {
      if (this.isSyncing) return;
      this.results = [];
      this.isDone = false;
      this.progress = { current: 0, total: this.sortedCards.length };
    }
  },
  methods: {
    syncTimestamp(card) {
      return card.trtDataDate ? new Date(card.trtDataDate).getTime() : 0;
    },
    // Collect a patient's TRT info from the EMR and persist it. Each collector
    // fetches its slice and adds a sibling key to `trtData`; the frontend derives
    // dose, qADAM and lab status from these on the card.
    async syncPatient(card) {
      if (!card.ehrId) {
        throw new Error('No EMR link');
      }
      const [{ medications }, { documents }, { orders, results }] = await Promise.all([
        getEmrMedications(card.ehrId),
        getEmrDocuments(card.ehrId),
        getEmrLabs(card.ehrId)
      ]);
      const trtData = { medications, documents, labs: { orders, results } };
      await api.syncTrtData(card.patientId, trtData);
    },
    async run() {
      if (this.isSyncing) return;
      const cards = this.sortedCards;
      this.progress = { current: 0, total: cards.length };
      this.results = [];
      this.isDone = false;
      this.isSyncing = true;

      for (let i = 0; i < cards.length; i++) {
        const card = cards[i];
        const name = `${card.firstName || ''} ${card.lastName || ''}`.trim() || 'Unknown';
        try {
          await this.syncPatient(card);
          this.results.push({ name, status: 'ok', message: 'Synced' });
        } catch (err) {
          const status = err.message === 'No EMR link' ? 'skipped' : 'error';
          this.results.push({ name, status, message: err.message });
          console.error(`Failed to sync TRT patient ${name}:`, err);
        }
        this.progress.current = i + 1;
        if (i < cards.length - 1) {
          await delay(SYNC_DELAY_MS);
        }
      }

      this.isSyncing = false;
      this.isDone = true;
      queryClient.invalidateQueries({ queryKey: ['trt-cards'] });
      this.$emit('done');
    }
  }
};
</script>

<style scoped>
.subtitle {
  font-size: 14px;
  color: #6b7280;
  margin-top: 8px;
}

.modal-card-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.stage-filter {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stage-filter .form-label {
  margin: 0;
}

.progress-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 8px;
}

.progress-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.progress-label {
  font-size: 14px;
  color: var(--text-secondary);
}

.progress-count {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.progress-bar-container {
  width: 100%;
  height: 8px;
  background-color: var(--border-color);
  border-radius: var(--border-radius);
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: var(--primary-color);
  transition: width 0.3s ease;
  border-radius: var(--border-radius);
}

.summary-counts {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.summary-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 999px;
}

.summary-chip.ok {
  background-color: #d6eba3;
  color: #2f5d18;
}

.summary-chip.skipped {
  background-color: #eceef0;
  color: #6b7280;
}

.summary-chip.error {
  background-color: #f6c5cb;
  color: #6f1b22;
}

.summary-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 220px;
  overflow-y: auto;
}

.summary-list li {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 13px;
  padding: 6px 10px;
  border-radius: var(--border-radius);
  background-color: #f9fafb;
}

.summary-name {
  font-weight: 600;
  color: #1f2937;
}

.summary-message {
  color: #6b7280;
}

.summary-list li.error .summary-message {
  color: #b91c1c;
}
</style>
